/**
 * useSimulation.js - Live Social Network Simulation Hook
 *
 * Simulates a bustling social network with organic timing and events.
 * Implements the "Backstage Buffer" strategy to inject new content
 * without spamming the Pexels API.
 *
 * @author GJYL Platform
 */

import { useEffect, useRef, useCallback } from 'react';

// ============================================
// SIMULATION CONFIGURATION
// ============================================

const SIMULATION_CONFIG = {
  // Micro-interactions (likes, notifications)
  microInteraction: {
    minDelay: 5000,   // 5 seconds
    maxDelay: 15000,  // 15 seconds
    likeChance: 0.7,  // 70% chance for new like
    notificationChance: 0.3 // 30% chance for notification
  },

  // Macro-interactions (new posts)
  macroInteraction: {
    minDelay: 45000,  // 45 seconds
    maxDelay: 90000   // 90 seconds (1.5 minutes)
  },

  // Chat activity
  chatActivity: {
    minDelay: 120000, // 2 minutes
    maxDelay: 180000  // 3 minutes
  }
};

// ============================================
// SLANG & CONTENT BANKS
// ============================================

const CHAT_MESSAGES = [
  "Bro did you see that new post?",
  "Your latest drop was insane 🔥",
  "Wanna hop in a call?",
  "Just finished this, thoughts?",
  "The algorithm blessed me today",
  "No cap your content is elite",
  "That setup is chef's kiss",
  "Catch me online later?",
  "This new update hits different fr",
  "Sending you the files now ✨",
  "I'm deceased this is so good 💀",
  "Living for these vibes",
  "The way I RAN here",
  "Absolutely unhinged content",
  "Manifesting this energy",
  "Touch grass? Never heard of it",
  "Built different fr fr",
  "The talent jumped out",
  "Periodt. 💯",
  "Main character energy only"
];

const NOTIFICATION_MESSAGES = {
  follow: ['started following you', 'is now following you', 'followed you back'],
  like: ['liked your post', 'loved your latest post', 'reacted to your post'],
  comment: ['commented on your post', 'replied to your post', 'mentioned you']
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get random delay between min and max (organic timing)
 */
function getRandomDelay(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Pick random item from array
 */
function pickRandom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Generate random like increment (1-5)
 */
function getRandomLikeIncrement() {
  const weights = [0.5, 0.25, 0.15, 0.07, 0.03]; // More weight on 1-2 likes
  const random = Math.random();
  let cumulative = 0;

  for (let i = 0; i < weights.length; i++) {
    cumulative += weights[i];
    if (random < cumulative) return i + 1;
  }

  return 1;
}

// ============================================
// MAIN HOOK
// ============================================

/**
 * useSimulation - The Game Loop Hook
 *
 * @param {object} params - Configuration object
 * @param {array} params.users - Array of Ghost Users
 * @param {array} params.contentBuffer - Buffer of pre-fetched posts
 * @param {function} params.setFeed - Feed state setter
 * @param {function} params.setNotifications - Notifications state setter
 * @param {function} params.setSpaces - Spaces state setter (optional)
 * @param {function} params.setChats - Chats state setter (optional)
 * @param {boolean} params.enabled - Enable/disable simulation (default: true)
 */
export function useSimulation({
  users = [],
  contentBuffer = [],
  setFeed,
  setNotifications,
  setSpaces,
  setChats,
  enabled = true
}) {
  // Use refs to track state without causing re-renders
  const bufferRef = useRef([...contentBuffer]);
  const timersRef = useRef({
    micro: null,
    macro: null,
    chat: null
  });
  const statsRef = useRef({
    likesAdded: 0,
    postsInjected: 0,
    notificationsCreated: 0,
    messagesAdded: 0
  });

  // ============================================
  // EVENT: MICRO-INTERACTIONS (Likes & Notifications)
  // ============================================

  const triggerMicroInteraction = useCallback(() => {
    if (!enabled || users.length === 0) return;

    const config = SIMULATION_CONFIG.microInteraction;
    const random = Math.random();

    if (random < config.likeChance) {
      // NEW LIKE: Increment likes on a random feed post
      setFeed(prevFeed => {
        if (prevFeed.length === 0) return prevFeed;

        const newFeed = [...prevFeed];
        const randomIndex = Math.floor(Math.random() * Math.min(10, newFeed.length)); // Focus on top 10 posts
        const post = newFeed[randomIndex];

        if (post) {
          const increment = getRandomLikeIncrement();
          newFeed[randomIndex] = {
            ...post,
            likes: post.likes + increment
          };
          statsRef.current.likesAdded += increment;
        }

        return newFeed;
      });
    } else if (random < config.likeChance + config.notificationChance) {
      // NEW NOTIFICATION
      const randomUser = pickRandom(users);
      const type = pickRandom(['follow', 'like', 'comment']);
      const message = `${randomUser.username} ${pickRandom(NOTIFICATION_MESSAGES[type])}`;

      setNotifications(prevNotifications => {
        const newNotification = {
          id: `notif-sim-${Date.now()}-${Math.random()}`,
          userId: randomUser.id,
          username: randomUser.username,
          avatar: randomUser.avatar_url,
          gradient: randomUser.gradient,
          type,
          message,
          timestamp: new Date(),
          isRead: false
        };

        statsRef.current.notificationsCreated++;
        return [newNotification, ...prevNotifications];
      });
    }

    // Schedule next micro-interaction (organic timing)
    const nextDelay = getRandomDelay(config.minDelay, config.maxDelay);
    timersRef.current.micro = setTimeout(triggerMicroInteraction, nextDelay);
  }, [enabled, users, setFeed, setNotifications]);

  // ============================================
  // EVENT: MACRO-INTERACTIONS (New Posts)
  // ============================================

  const triggerMacroInteraction = useCallback(() => {
    if (!enabled || bufferRef.current.length === 0) {
      // Buffer empty - schedule retry
      const config = SIMULATION_CONFIG.macroInteraction;
      const nextDelay = getRandomDelay(config.minDelay, config.maxDelay);
      timersRef.current.macro = setTimeout(triggerMacroInteraction, nextDelay);
      return;
    }

    // Take one post from buffer
    const newPost = bufferRef.current.shift();

    if (newPost) {
      // Update timestamp to be "just now"
      const freshPost = {
        ...newPost,
        timestamp: new Date(),
        minutesAgo: 0,
        // Assign fresh ID to avoid duplicates
        id: `feed-sim-${Date.now()}-${Math.random()}`,
        // Reset engagement to simulate "fresh" post
        likes: Math.floor(Math.random() * 100) + 10,
        comments: Math.floor(Math.random() * 20) + 2,
        shares: Math.floor(Math.random() * 10)
      };

      // Inject at top of feed
      setFeed(prevFeed => {
        statsRef.current.postsInjected++;
        console.log(`🎬 New post injected! (${bufferRef.current.length} left in buffer)`);
        return [freshPost, ...prevFeed];
      });
    }

    // Schedule next macro-interaction
    const config = SIMULATION_CONFIG.macroInteraction;
    const nextDelay = getRandomDelay(config.minDelay, config.maxDelay);
    timersRef.current.macro = setTimeout(triggerMacroInteraction, nextDelay);
  }, [enabled, setFeed]);

  // ============================================
  // EVENT: CHAT ACTIVITY
  // ============================================

  const triggerChatActivity = useCallback(() => {
    if (!enabled || users.length === 0) return;

    // Option 1: Update a chat's last message
    if (setChats) {
      setChats(prevChats => {
        if (prevChats.length === 0) return prevChats;

        const newChats = [...prevChats];
        const randomIndex = Math.floor(Math.random() * Math.min(5, newChats.length)); // Focus on top 5 chats
        const chat = newChats[randomIndex];

        if (chat) {
          newChats[randomIndex] = {
            ...chat,
            lastMessage: pickRandom(CHAT_MESSAGES),
            lastMessageTime: new Date(),
            status: Math.random() < 0.3 ? 'typing' : chat.status, // 30% chance of typing indicator
            unread: Math.random() < 0.5 ? (chat.unread || 0) + 1 : chat.unread // 50% chance to increment unread
          };
          statsRef.current.messagesAdded++;
        }

        // Re-sort by most recent
        return newChats.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
      });
    }

    // Option 2: Update a space (if setSpaces provided)
    if (setSpaces && Math.random() < 0.3) { // 30% chance
      setSpaces(prevSpaces => {
        if (prevSpaces.length === 0) return prevSpaces;

        const newSpaces = [...prevSpaces];
        const randomIndex = Math.floor(Math.random() * newSpaces.length);
        const space = newSpaces[randomIndex];

        if (space) {
          newSpaces[randomIndex] = {
            ...space,
            unreadMessages: (space.unreadMessages || 0) + Math.floor(Math.random() * 3) + 1
          };
        }

        return newSpaces;
      });
    }

    // Schedule next chat activity
    const config = SIMULATION_CONFIG.chatActivity;
    const nextDelay = getRandomDelay(config.minDelay, config.maxDelay);
    timersRef.current.chat = setTimeout(triggerChatActivity, nextDelay);
  }, [enabled, users, setChats, setSpaces]);

  // ============================================
  // LIFECYCLE: START/STOP SIMULATION
  // ============================================

  useEffect(() => {
    if (!enabled) {
      // Clear all timers if disabled
      Object.values(timersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      return;
    }

    console.log('🎮 Starting Social Network Simulation...');
    console.log(`  📦 Content Buffer: ${bufferRef.current.length} posts ready`);
    console.log(`  👥 Ghost Users: ${users.length} active`);

    // Initialize buffer ref
    bufferRef.current = [...contentBuffer];

    // Start all event loops with staggered timing
    const microDelay = getRandomDelay(
      SIMULATION_CONFIG.microInteraction.minDelay,
      SIMULATION_CONFIG.microInteraction.maxDelay
    );
    timersRef.current.micro = setTimeout(triggerMicroInteraction, microDelay);

    const macroDelay = getRandomDelay(
      SIMULATION_CONFIG.macroInteraction.minDelay,
      SIMULATION_CONFIG.macroInteraction.maxDelay
    );
    timersRef.current.macro = setTimeout(triggerMacroInteraction, macroDelay);

    const chatDelay = getRandomDelay(
      SIMULATION_CONFIG.chatActivity.minDelay,
      SIMULATION_CONFIG.chatActivity.maxDelay
    );
    timersRef.current.chat = setTimeout(triggerChatActivity, chatDelay);

    // Cleanup on unmount
    return () => {
      console.log('🛑 Stopping Social Network Simulation');
      console.log('📊 Simulation Stats:');
      console.log(`  - ${statsRef.current.likesAdded} likes added`);
      console.log(`  - ${statsRef.current.postsInjected} posts injected`);
      console.log(`  - ${statsRef.current.notificationsCreated} notifications created`);
      console.log(`  - ${statsRef.current.messagesAdded} messages added`);

      Object.values(timersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [enabled, contentBuffer, users, triggerMicroInteraction, triggerMacroInteraction, triggerChatActivity]);

  // ============================================
  // PUBLIC API
  // ============================================

  return {
    stats: statsRef.current,
    bufferRemaining: bufferRef.current.length
  };
}

// Default export
export default useSimulation;
